import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faCircleCheck, faCircleExclamation, faPause, faStop, faUsers, faUndo, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import ConfirmationModal from '../../components/ConfirmationModal';
import SuccessModal from '../../components/SuccessModal';

const ArchiveInterviewers = () => {
    const [archivedInterviewers, setArchivedInterviewers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [restoring, setRestoring] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [interviewerToRestore, setInterviewerToRestore] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchArchivedInterviewers();
    }, []);

    const fetchArchivedInterviewers = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get('/interviewers/archived');
            console.log('Archived interviewers response:', response.data);
            if (response.data.success) {
                setArchivedInterviewers(response.data.data);
            } else {
                setError('Erreur lors du chargement des enquêteurs archivés');
            }
        } catch (error) {
            setError('Erreur lors du chargement des enquêteurs archivés');
            console.error('Error fetching archived interviewers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRestoreClick = (interviewer) => {
        setInterviewerToRestore(interviewer);
        setShowConfirmModal(true);
    };

    const handleRestore = async () => {
        if (!interviewerToRestore) return;

        try {
            setRestoring(interviewerToRestore.id);
            console.log('Restoring interviewer with ID:', interviewerToRestore.id);
            // `source` tells the API which table this row came from: a soft-archived interviewer
            // is restored in place, while a legacy row is re-created from the archive copy.
            const response = await api.post(`/interviewers/archived/${interviewerToRestore.id}/restore`, {
                source: interviewerToRestore.source || 'interviewer',
            });
            console.log('Restore response:', response.data);
            
            setSuccessMessage(`${interviewerToRestore.name} a été restauré avec succès`);
            setShowConfirmModal(false);
            setInterviewerToRestore(null);
            fetchArchivedInterviewers(); // Refresh the list
            
            // Clear success message after 5 seconds
            setTimeout(() => {
                setSuccessMessage('');
            }, 5000);
        } catch (error) {
            console.error('Error restoring interviewer:', error);
            const errorMessage = error.response?.data?.message || 'Erreur lors de la restauration de l\'enquêteur';
            setError(errorMessage);
        } finally {
            setRestoring(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 relative overflow-hidden flex items-center justify-center">
                {/* Dotted Background */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>
                </div>
                <div className="text-center relative z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement des enquêteurs archivés...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden py-8">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white shadow-xl rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Enquêteurs Archivés</h1>
                            <p className="mt-1 text-sm text-gray-600">
                                Historique des enquêteurs supprimés
                            </p>
                        </div>
                        <Link
                            to="/interviewers"
                            className="w-full sm:w-auto bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center justify-center sm:justify-start"
                        >
                            <FontAwesomeIcon icon={faUsers} className="mr-2" />
                            Enquêteurs
                        </Link>
                    </div>

                    {error && (
                        <div className="px-6 py-4 bg-red-50 border-l-4 border-red-400">
                            <p className="text-red-700">{error}</p>
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Référence
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Nom
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Zone
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Villes
                                    </th>
                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                        Statut
                                    </th>
                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                        Vérifié
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Archivé le
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Archivé par
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Raison
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {archivedInterviewers.map((interviewer) => (
                                    <tr key={interviewer.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {interviewer.ref}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {interviewer.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {interviewer.email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {interviewer.zone ? (
                                                <span className={`px-2 py-1 text-xs rounded-full ${
                                                    interviewer.zone === 'rural' 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {interviewer.zone === 'rural' ? 'Rural' : 'Urbain'}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {Array.isArray(interviewer.ville) && interviewer.ville.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {interviewer.ville.map((ville, index) => (
                                                        <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                                            {ville}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : interviewer.ville ? (
                                                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                                    {interviewer.ville}
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-center">
                                            <div className="flex items-center justify-center">
                                                <div className="relative group">
                                                    <FontAwesomeIcon 
                                                        icon={
                                                            interviewer.status === 'active' 
                                                                ? faCircleCheck 
                                                                : interviewer.status === 'suspended'
                                                                ? faPause
                                                                : faStop
                                                        }
                                                        className={
                                                            interviewer.status === 'active' 
                                                                ? 'text-green-600' 
                                                                : interviewer.status === 'suspended'
                                                                ? 'text-red-600'
                                                                : 'text-yellow-600'
                                                        }
                                                    />
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                        {interviewer.status === 'active' ? 'Actif' : 
                                                         interviewer.status === 'suspended' ? 'Suspendu' : 'Inactif'}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-center">
                                            <div className="flex items-center justify-center">
                                                <div className="relative group">
                                                    <FontAwesomeIcon 
                                                        icon={
                                                            interviewer.verified === 'yes' 
                                                                ? faCircleCheck 
                                                                : interviewer.verified === 'partially'
                                                                ? faCircleExclamation
                                                                : faCircleXmark
                                                        }
                                                        className={
                                                            interviewer.verified === 'yes' 
                                                                ? 'text-green-600' 
                                                                : interviewer.verified === 'partially'
                                                                ? 'text-yellow-600'
                                                                : 'text-gray-500'
                                                        }
                                                    />
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                        {interviewer.verified === 'yes' ? 'Vérifié' : 
                                                         interviewer.verified === 'partially' ? 'Partiellement vérifié' : 'Non vérifié'}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(interviewer.archived_at).toLocaleDateString('fr-FR', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {interviewer.archived_by_name}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                                            <div className="truncate" title={interviewer.archive_reason}>
                                                {interviewer.archive_reason}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => handleRestoreClick(interviewer)}
                                                disabled={restoring === interviewer.id}
                                                className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                            >
                                                {restoring === interviewer.id ? (
                                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin w-4 h-4" />
                                                ) : (
                                                    <FontAwesomeIcon icon={faUndo} className="w-4 h-4" />
                                                )}
                                                <span>Restaurer</span>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {archivedInterviewers.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-gray-500">Aucun enquêteur archivé trouvé</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="fixed bottom-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg z-50">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {successMessage}
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg z-50">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {error}
                        <button 
                            onClick={() => setError('')}
                            className="ml-2 text-red-600 hover:text-red-800"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showConfirmModal}
                onClose={() => {
                    setShowConfirmModal(false);
                    setInterviewerToRestore(null);
                }}
                onConfirm={handleRestore}
                title="Restaurer l'enquêteur"
                message={`Êtes-vous sûr de vouloir restaurer l'enquêteur "${interviewerToRestore?.name}" ? Il redeviendra actif et sera supprimé de cette liste d'archives.`}
                confirmText="Restaurer"
                cancelText="Annuler"
                confirmColor="bg-red-600 hover:bg-red-700"
                loading={restoring === interviewerToRestore?.id}
            />
        </div>
    );
};

export default ArchiveInterviewers;
